/* eslint-disable */
import { useState, useEffect, useRef, useContext } from "react";
import {safeLocalStorage} from "../../../util/safeStorage";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { BaseMessage, BaseChatSession } from "./types";

// Generic chat state hook - preserves all existing state patterns
export const useChatState = <TSession extends BaseChatSession = BaseChatSession>() => {
    const [message, setMessage] = useState("");
    const [chatSession, setChatSession] = useState<TSession | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showNewConversationButton, setShowNewConversationButton] = useState(false);

    return {
        message,
        setMessage,
        chatSession,
        setChatSession,
        isLoading,
        setIsLoading,
        error,
        setError,
        showNewConversationButton,
        setShowNewConversationButton
    };
};

// Message handling hook - preserves existing message logic
export const useMessageHandling = () => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-scroll to bottom (existing behavior)
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Focus input (existing behavior)
    const focusInput = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    return {
        messagesEndRef,
        messagesContainerRef,
        inputRef,
        scrollToBottom,
        focusInput
    };
};

// Auth integration hook - preserves existing auth patterns
export const useChatAuth = () => {
    const { user } = useContext(AuthContext);

    return {
        user,
        isLoggedIn: !!user?._candidate?.id,
        candidateId: user?._candidate?.id
    };
};

// Error handling hook - standardizes existing error patterns
export const useErrorHandling = () => {
    const handleError = (error: any): string => {
        // Preserve existing error handling logic from all chat components
        if (error?.isAxiosError || (error?.response && error?.config)) {
            if (error.response?.status === 404) {
                return "Your session has expired. Please refresh the page to start a new conversation.";
            } else if (error.response?.status >= 500) {
                return "Server error. Please try again later.";
            } else {
                return "Failed to send your message. Please try again.";
            }
        }
        return "Sorry, there was an error processing your message. Please try again.";
    };

    const createErrorMessage = (errorText: string): BaseMessage => {
        return {
            id: Date.now().toString(),
            text: errorText,
            sender: "agent",
            timestamp: new Date(),
        };
    };

    return {
        handleError,
        createErrorMessage
    };
};

// Message creation utilities - preserves existing message patterns
export const useMessageUtils = () => {
    const createUserMessage = (text: string): BaseMessage => {
        return {
            id: Date.now().toString(),
            text,
            sender: "user",
            timestamp: new Date(),
        };
    };

    const createAgentMessage = (text: string, offset: number = 1): BaseMessage => {
        return {
            id: (Date.now() + offset).toString(),
            text,
            sender: "agent",
            timestamp: new Date(),
        };
    };

    return {
        createUserMessage,
        createAgentMessage
    };
};

// Keyboard handling hook - preserves existing Enter key behavior
export const useKeyboardHandling = (onSend: () => void) => {
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === "Enter") {
            // For TextArea, check if Shift is not pressed (preserve existing behavior)
            if (e.currentTarget.tagName === 'TEXTAREA' && e.shiftKey) {
                return; // Allow new line with Shift+Enter
            }
            e.preventDefault();
            onSend();
        }
    };

    return { handleKeyPress };
};

// Auto-focus hook - preserves existing focus behavior
export const useAutoFocus = (isLoading: boolean, inputRef: React.RefObject<HTMLInputElement>) => {
    useEffect(() => {
        if (!isLoading && inputRef.current) {
            // Add small delay to ensure DOM is ready (preserves existing behavior)
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            }, 100);
        }
    }, [isLoading]);
};

// Auto-scroll hook - preserves existing scroll behavior
export const useAutoScroll = (messages: BaseMessage[], enabled: boolean = true, dependencies: any[] = []) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (enabled) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, enabled, ...dependencies]);

    return { messagesEndRef };
};

// Local storage hook - preserves existing localStorage patterns
export const useLocalStorage = <T>(key: string, defaultValue: T) => {
    const [value, setValue] = useState<T>(() => {
        try {
            const item = safeLocalStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
            return defaultValue;
        }
    });

    const setStoredValue = (newValue: T) => {
        try {
            setValue(newValue);
            safeLocalStorage.setItem(key, JSON.stringify(newValue));
        } catch (error) {
            console.error(`Error setting localStorage key "${key}":`, error);
        }
    };

    const removeStoredValue = () => {
        try {
            setValue(defaultValue);
            safeLocalStorage.removeItem(key);
        } catch (error) {
            console.error(`Error removing localStorage key "${key}":`, error);
        }
    };

    return [value, setStoredValue, removeStoredValue] as const;
};

// Session management hook - can be gradually adopted
export interface SessionConfig {
    onSessionStart?: () => void;
    onSessionEnd?: () => void;
    onSessionError?: (error: string) => void;
}

export const useSessionManagement = (config: SessionConfig = {}) => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [isSessionActive, setIsSessionActive] = useState(false);

    const startSession = (id: string) => {
        setSessionId(id);
        setIsSessionActive(true);
        config.onSessionStart?.();
    };

    const endSession = () => {
        setSessionId(null);
        setIsSessionActive(false);
        config.onSessionEnd?.();
    };

    const handleSessionError = (error: string) => {
        setIsSessionActive(false);
        config.onSessionError?.(error);
    };

    return {
        sessionId,
        isSessionActive,
        startSession,
        endSession,
        handleSessionError
    };
};

// Transition hook - preserves existing animation patterns
export const useTransitions = () => {
    const [isTransitioning, setIsTransitioning] = useState(false);

    const startTransition = (callback?: () => void, delay: number = 300) => {
        setIsTransitioning(true);
        setTimeout(() => {
            callback?.();
            setIsTransitioning(false);
        }, delay);
    };

    return {
        isTransitioning,
        startTransition
    };
};

export interface ChatSession {
    id: string;
    created_at: string;
    user_id: number;
    conversation_type: string;
    title: string;
    status: string;
    context_data: any;
    updated_at: number;
    last_activity: number;
    job_demand_id?: number;
}

export const useChatSessions = (conversationType?: 'job_coach' | 'job_interview' | 'job_letter' | 'job_search' | 'job_tax') => {
    const {
        data: sessions = [],
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['chat-sessions', conversationType],
        queryFn: async () => {
            if (conversationType === 'job_interview') {
                const { getXanoAiInterviewSessions } = await import('../../../services/api');
                return getXanoAiInterviewSessions();
            } else if (conversationType === 'job_letter') {
                const { getXanoAiCoverLetterSessions } = await import('../../../services/api');
                return getXanoAiCoverLetterSessions();
            } else if (conversationType === 'job_search') {
                const { getXanoAiJobSearchSessions } = await import('../../../services/api');
                return getXanoAiJobSearchSessions();
            } else if (conversationType === 'job_tax') {
                const { getXanoAiRentensteuerberaterSessions } = await import('../../../services/api');
                return getXanoAiRentensteuerberaterSessions();
            } else {
                const { getXanoAiSessions } = await import('../../../services/api');
                return getXanoAiSessions();
            }
        },
        staleTime: 30 * 1000,
    });

    return {
        sessions: Array.isArray(sessions) ? sessions : [],
        isLoading,
        error,
        refetch
    };
};

export const useSessionMessages = (sessionId: string | null, conversationType?: 'job_coach' | 'job_interview' | 'job_letter' | 'job_search' | 'job_tax') => {
    const {
        data: messages = [],
        isLoading,
        error,
        refetch
    } = useQuery({
        queryKey: ['session-messages', sessionId, conversationType],
        queryFn: async () => {
            if (!sessionId) return [];
            if (conversationType === 'job_interview') {
                const { getXanoAiInterviewSessionMessages } = await import('../../../services/api');
                return getXanoAiInterviewSessionMessages(sessionId);
            } else if (conversationType === 'job_letter') {
                const { getXanoAiCoverLetterSessionMessages } = await import('../../../services/api');
                return getXanoAiCoverLetterSessionMessages(sessionId);
            } else if (conversationType === 'job_search') {
                const { getXanoAiJobSearchSessionMessages } = await import('../../../services/api');
                return getXanoAiJobSearchSessionMessages(sessionId);
            } else if (conversationType === 'job_tax') {
                const { getXanoAiRentensteuerberaterSessionMessages } = await import('../../../services/api');
                return getXanoAiRentensteuerberaterSessionMessages(sessionId);
            } else {
                const { getXanoAiSessionMessages } = await import('../../../services/api');
                return getXanoAiSessionMessages(sessionId);
            }
        },
        enabled: !!sessionId,
        staleTime: 30 * 1000,
    });

    return {
        messages,
        isLoading,
        error,
        refetch
    };
};

export const useCreateSession = (conversationType?: 'job_coach' | 'job_interview' | 'job_letter' | 'job_search' | 'job_tax') => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (jobRole?: string) => {
            if (conversationType === 'job_interview') {
                const { startXanoAiInterviewSession } = await import('../../../services/api');
                const response = await startXanoAiInterviewSession(jobRole || null);
                return {
                    sessionId: response.session_id,
                    initialMessage: response.message
                };
            } else if (conversationType === 'job_letter') {
                const { startXanoAiCoverLetterSession } = await import('../../../services/api');
                const response = await startXanoAiCoverLetterSession(jobRole || null);
                return {
                    sessionId: response.session_id,
                    initialMessage: response.message
                };
            } else if (conversationType === 'job_search') {
                const { startXanoAiJobSearchSession } = await import('../../../services/api');
                const response = await startXanoAiJobSearchSession();
                return {
                    sessionId: response.session_id,
                    initialMessage: response.message
                };
            } else if (conversationType === 'job_tax') {
                const { startXanoAiRentensteuerberaterSession } = await import('../../../services/api');
                const response = await startXanoAiRentensteuerberaterSession();
                return {
                    sessionId: response.session_id,
                    initialMessage: response.message
                };
            } else {
                const { startXanoAiSession } = await import('../../../services/api');
                const response = await startXanoAiSession();
                return {
                    sessionId: response.session_id,
                    initialMessage: response.message
                };
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chat-sessions', conversationType] });
        }
    });

    return {
        createSession: mutation.mutate,
        isCreating: mutation.isPending,
        error: mutation.error
    };
};

export const useDeleteSession = (conversationType?: 'job_coach' | 'job_interview' | 'job_letter' | 'job_search' | 'job_tax') => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (sessionId: string) => {
            const { deleteXanoAiSession } = await import('../../../services/api');
            return deleteXanoAiSession(sessionId);
        },
        onSuccess: () => {
            // Invalidate all chat-sessions queries
            queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
            // Also invalidate the specific conversation type
            queryClient.invalidateQueries({ queryKey: ['chat-sessions', conversationType] });
        }
    });

    return {
        deleteSession: mutation.mutate,
        isDeleting: mutation.isPending,
        error: mutation.error
    };
};

// Hook to update session title when conversation reaches 5 messages
export const useSessionTitleUpdater = (
    sessionId: string | null,
    messages: any[],
    conversationType?: 'job_coach' | 'job_interview' | 'job_letter' | 'job_search' | 'job_tax'
) => {
    const queryClient = useQueryClient();
    const prevMessageCount = useRef<number>(0);

    useEffect(() => {
        if (prevMessageCount.current <= 4 && messages.length >= 5 && sessionId) {
            queryClient.invalidateQueries({ queryKey: ['chat-sessions'] });
            queryClient.invalidateQueries({ queryKey: ['chat-sessions', conversationType] });
        }

        prevMessageCount.current = messages.length;
    }, [messages.length, sessionId, queryClient]);

    // Reset count when session changes
    useEffect(() => {
        prevMessageCount.current = 0;
    }, [sessionId]);
};

export const useSessionJobDetails = (sessionId: string | null, conversationType?: string) => {
    const {
        data: jobDetails,
        isLoading,
        error
    } = useQuery({
        queryKey: ['session-job-details', sessionId, conversationType],
        queryFn: async () => {
            if (!sessionId || conversationType !== 'job_letter') {
                return null;
            }

            // First, get the session to find the job_demand_id
            const { getXanoAiCoverLetterSessions, getJobById } = await import('../../../services/api');
            const sessions = await getXanoAiCoverLetterSessions();
            const session = sessions.find((s: any) => s.id === sessionId);

            if (!session?.job_demand_id) {
                return null;
            }

            // Fetch the job details
            const jobDetails = await getJobById(session.job_demand_id.toString());
            return {
                jobId: session.job_demand_id.toString(),
                jobTitle: jobDetails.title || 'Job Position',
                jobDescription: jobDetails.description || '',
                jobRole: [jobDetails.title, jobDetails.description].filter(Boolean).join('\n\n')
            };
        },
        enabled: !!sessionId && conversationType === 'job_letter',
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        gcTime: 10 * 60 * 1000  // Keep in cache for 10 minutes
    });

    return {
        jobDetails,
        isLoading,
        error
    };
};

// Hook for smart session loading - returns resolved sessionId or loading state
export const useSmartSessionLoader = (
    conversationType: 'job_coach' | 'job_interview' | 'job_letter' | 'job_search' | 'job_tax',
    urlSessionId: string | null
) => {
    const navigate = useNavigate();
    const { sessions = [], isLoading, error } = useChatSessions(conversationType);
    const [hasNavigated, setHasNavigated] = useState(false);

    // Handle navigation in useEffect to avoid calling navigate during render
    useEffect(() => {
        // Only navigate when we have sessions loaded and haven't navigated yet and no URL sessionId
        if (!urlSessionId && !isLoading && sessions.length > 0 && !hasNavigated) {
            const mostRecentSession = sessions
                .filter((session: ChatSession) => session.status === 'active')
                .sort((a: ChatSession, b: ChatSession) => b.last_activity - a.last_activity)[0];

            if (mostRecentSession) {
                const routePath = conversationType === 'job_interview'
                    ? '/interview'
                    : conversationType === 'job_coach'
                        ? '/job-coach'
                        : conversationType === 'job_search'
                            ? '/ai-jobs'
                            : conversationType === 'job_tax'
                                ? '/rentensteuerberater'
                                : '/cover-letter';

                setHasNavigated(true);
                navigate(`${routePath}/${mostRecentSession.id}`, { replace: true });
            }
        }
    }, [urlSessionId, isLoading, sessions, hasNavigated, conversationType, navigate]);

    useEffect(() => {
        if (urlSessionId) {
            setHasNavigated(false);
        }
    }, [urlSessionId]);

    if (urlSessionId) {
        return {
            resolvedSessionId: urlSessionId,
            isLoading: false,
            error: null
        };
    }

    // If still loading sessions or in the process of navigation, return loading state
    if (isLoading || (!urlSessionId && sessions.length > 0 && !hasNavigated)) {
        return {
            resolvedSessionId: null,
            isLoading: true,
            error: null
        };
    }

    // No sessions found - allow new session creation
    return {
        resolvedSessionId: null,
        isLoading: false,
        error: error
    };
};
